import { authFetch } from '../../api/csrf';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileText,
  X,
  AlertCircle,
  CheckCircle,
  MapPin,
  Briefcase,
  Users,
  Clock,
  Calendar,
  Building,
  Mail,
  Phone,
  User,
  GraduationCap,
  Award,
  Send,
  Star,
  Globe,
  Plus,
  XCircle,
  Search
} from 'lucide-react';
import DOMPurify from 'dompurify'; // Import DOMPurify
import { API_BASE_URL } from '../../api/config';

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [candidateStatus, setCandidateStatus] = useState(null);
  const [showCandidateStatusModal, setShowCandidateStatusModal] = useState(false);
  const [candidateSearchError, setCandidateSearchError] = useState(null);
  const pageSize = 10;

  const navigate = useNavigate();

  // Utility function to strip HTML tags for truncation
  const stripHTML = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // Utility function to get truncated description
  const getTruncatedDescription = (description, maxLength = 150) => {
    const plainText = stripHTML(description);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  useEffect(() => {

    loadJobs();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [jobs.length]);

  const loadJobs = async (searchTerm = '') => {
    try {
      setLoading(true);
      let jobsUrl = `${API_BASE_URL}/jobs/public`;

      // Add search query parameter if provided
      if (searchTerm && searchTerm.trim()) {
        jobsUrl += `?search=${encodeURIComponent(searchTerm.trim())}`;
      }



      const response = await authFetch(jobsUrl);
      const result = await response.json();

      if (result.responseCode === 200) {
        setJobs(result.data);
      } else {
        setError(result.responseMessage || 'Failed to load jobs');
      }
    } catch (err) {
      setError('Failed to load jobs. Please try again.');

    } finally {
      setLoading(false);
    }
  };

  // Utility function to validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Utility function to get human-readable recruitment status
  const getRecruitmentStatusText = (status) => {
    if (!status) return 'Applied';

    const statusMap = {
      'applied': 'Applied',
      'screening': 'Under Screening',
      'shortlisted': 'Shortlisted',
      'interview': 'Interview Scheduled',
      'interviewed': 'Interview Completed',
      'offer': 'Interview Scheduled',
      'offered': 'Offer Extended',
      'hired': 'Hired',
      'rejected': 'Rejected'
    };

    return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  const searchCandidateStatus = async (identifier) => {
    try {
      setIsSearching(true);
      setCandidateSearchError(null);

      // Encode the identifier to handle special characters in email
      const encodedIdentifier = encodeURIComponent(identifier.trim());
      const candidateUrl = `${API_BASE_URL}/jobs/public/candidate/${encodedIdentifier}`;


      const response = await authFetch(candidateUrl);
      const result = await response.json();

      if (result.responseCode === 200) {
        setCandidateStatus(result.data);
        setShowCandidateStatusModal(true);
      } else {
        const isEmail = validateEmail(identifier.trim());
        const errorMessage = isEmail
          ? result.responseMessage || 'Candidate not found. Please check your email address.'
          : result.responseMessage || 'Candidate not found. Please check your candidate ID.';
        setCandidateSearchError(errorMessage);
      }
    } catch (err) {
      setCandidateSearchError('Failed to search candidate status. Please try again.');

    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    let query = searchQuery.trim();

    // Remove any disallowed characters that might have been pasted
    const disallowedChars = /[<>\/"'=\\(){};:%#`]/g;
    query = query.replace(disallowedChars, '');

    // Update the search query if it was modified
    if (query !== searchQuery.trim()) {
      setSearchQuery(query);
    }

    if (!query) {
      // If search is empty, reload all jobs
      loadJobs();
      setCandidateStatus(null);
      setShowCandidateStatusModal(false);
      setCandidateSearchError(null);
      return;
    }

    // Check if the search query is a candidate ID (starts with "CAND") or an email
    const isCandidateId = query.toUpperCase().startsWith('CAND');
    const isEmail = validateEmail(query);

    if (isCandidateId || isEmail) {
      // Search for candidate status by candidate ID or email
      await searchCandidateStatus(query);
    } else {
      // Search for jobs
      await loadJobs(query);
      setCandidateStatus(null);
      setShowCandidateStatusModal(false);
      setCandidateSearchError(null);
    }
  };

  const handleSearchInputChange = (e) => {
    let value = e.target.value;

    // Remove disallowed characters: < > / " ' = \ ( ) { } ; : % + # `
    const disallowedChars = /[<>\/"'=\\(){};:%#`]/g;
    const filteredValue = value.replace(disallowedChars, '');

    // Only update state if the value actually changed (prevents unnecessary updates when disallowed chars are filtered)
    if (filteredValue !== searchQuery) {
      setSearchQuery(filteredValue);
      setCandidateSearchError(null);

      // If search is cleared, reload all jobs
      if (!filteredValue.trim()) {
        loadJobs();
        setCandidateStatus(null);
        setShowCandidateStatusModal(false);
      }
    }
  };

  const handleSearchKeyDown = (e) => {
    // Prevent disallowed characters from being entered
    const disallowedChars = /[<>\/"'=\\(){};:%#`]/;
    if (disallowedChars.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleViewJob = async (jobId) => {
    try {
      const jobUrl = `${API_BASE_URL}/jobs/public/${jobId}`;
      const response = await authFetch(jobUrl);
      const result = await response.json();

      if (result.responseCode === 200) {
        setSelectedJob(result.data);
      } else {
        setError(result.responseMessage || 'Failed to load job details');
      }
    } catch (err) {
      setError('Failed to load job details. Please try again.');

    }
  };

  const handleApplyNow = (job) => {
    // Use jobId if available (custom ID like JOB000002), otherwise use _id
    const jobIdentifier = job.jobId || job._id;
    const applicationUrl = `/job-application/${jobIdentifier}`;
    // Open in new tab
    window.open(applicationUrl, '_blank');
  };

  const formatSalary = (min, max, currency = 'INR') => {
    if (min === undefined || max === undefined) return 'Not specified';
    const formatNumber = (num) => {
      if (num >= 100000) {
        return `${(num / 100000).toFixed(1)}L`;
      } else if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
      }
      return num.toString();
    };

    if (min === 0 && max === 0) return 'Not specified';
    if (min === max) return `₹${formatNumber(min)}`;
    return `₹${formatNumber(min)} - ₹${formatNumber(max)}`;
  };

  const formatExperience = (experience) => {
    if (!experience) return 'Not specified';
    if (typeof experience === 'string') return experience;

    // Use name if available (e.g., "Fresher", "Experienced")
    if (experience.name) return experience.name;

    const min = experience.min ?? (Array.isArray(experience) ? experience[0] : null);
    const max = experience.max ?? (Array.isArray(experience) ? experience[1] : null);

    if (min != null && max != null) {
      if (min === max) return `${min} years`;
      return `${min}-${max} years`;
    }
    if (min != null) {
      return `${min}+ years`;
    }
    if (max != null) {
      return `Up to ${max} years`;
    }
    return 'Not specified';
  };

  const getApplicationsCount = (job) =>
    job?.applicationsCount ?? job?.applications ?? job?.totalApplications ?? 0;

  const getStatusBadgeClasses = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeClasses = (priority) => {
    if (!priority) return 'bg-gray-100 text-gray-800';
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.max(1, Math.ceil(jobs.length / pageSize));
  const paginatedJobs = jobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        {/* Decorative Circle Backgrounds */}
        <div className="absolute top-0 right-0 -mt-12 -mr-12 w-80 h-80 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>
        <div className="absolute -bottom-20 -left-10 w-96 h-96 bg-black/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="absolute inset-0 bg-black/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8 sm:py-16">
          <div className="flex flex-col md:flex-row items-start justify-between gap-4 sm:gap-8 md:gap-12">
            {/* Left Side: Logo and Company Info */}
            <div className="flex flex-col min-w-0 flex-1 w-full">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="bg-white p-1.5 sm:p-2 md:p-2.5 rounded-xl md:rounded-2xl shadow-xl flex items-center justify-center flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 transition-transform duration-300 hover:scale-105">
                  <img
                    src="/logo.png"
                    alt="Company Logo"
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl md:text-4xl font-extrabold text-white tracking-tight leading-snug break-words">
                    {process.env.REACT_APP_COMPANY_NAME || "Agrosaf Pharmaceuticals Pvt. Ltd."}
                  </h2>
                  <p className="text-[10px] sm:text-xs md:text-sm text-blue-100 font-semibold tracking-wider uppercase mt-0.5 md:mt-1">
                    Careers Portal
                  </p>
                </div>
              </div>

              {/* Divider & Stats under the Left Column */}
              <div className="border-t border-white/20 mt-6 pt-5 flex flex-wrap gap-2 sm:gap-3">
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 shadow-sm hover:bg-white/15 transition-all duration-200">
                  <Briefcase className="h-4 w-4 text-white" />
                  <span className="text-xs font-semibold text-white">
                    {jobs.length} Active Jobs
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 shadow-sm hover:bg-white/15 transition-all duration-200">
                  <MapPin className="h-4 w-4 text-white" />
                  <span className="text-xs font-semibold text-white">
                    {new Set(jobs.map(j => j.location || j.region).filter(Boolean)).size || 1} Locations
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side: Hero Content */}
            <div className="text-center md:text-left max-w-xl flex-1 md:border-l md:border-white/10 md:pl-8 mt-6 md:mt-0">
              <h2 className="text-3xl sm:text-4xl text-center md:text-left font-extrabold text-white mb-4 leading-none">
                Join <span className="text-yellow-300">Us</span>
              </h2>
              <div className="pl-4 border-l-2 border-yellow-300/50 py-1 inline-block text-left">
                <p className="text-sm sm:text-base lg:text-lg text-blue-100 leading-relaxed font-medium">
                  Discover exciting career opportunities and join our innovative team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar Section */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search jobs, ID or Email"
                maxLength={150}
                className="w-full pl-12 pr-16 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                {searchQuery.length}/150
              </div>
            </div>
            <button
              type="submit"
              disabled={isSearching}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {isSearching ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="h-5 w-5" />
                  <span>Search</span>
                </>
              )}
            </button>
          </form>
          {candidateSearchError && (
            <div className="mt-3">
              <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                {candidateSearchError}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 text-lg">Loading job opportunities...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error</h3>
                <p className="text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Jobs Grid */}
        {!loading && !error && paginatedJobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-8">
            {paginatedJobs.map((job) => (
              <div
                key={job._id}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
              >
                {/* Job Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 capitalize break-words break-all max-w-[18rem] sm:max-w-[20rem] lg:max-w-[22rem]">
                        {job.title}
                      </h3>
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Building className="h-4 w-4 mr-2 text-blue-600" />
                        <span className="capitalize break-words break-all max-w-full">
                          {job.department || 'Not specified'}
                        </span>
                      </div>
                    </div>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                      Active
                    </span>
                  </div>

                  {/* Job Details */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="capitalize break-words">{job.location || 'Not specified'}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="capitalize break-words">{job.type || 'Not specified'}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <GraduationCap className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="capitalize break-words">{formatExperience(job.experience)}</span>
                    </div>
                    {formatSalary(job.salary?.min, job.salary?.max) !== 'Not specified' && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="text-gray-400 font-bold mr-2">₹</span>
                        <span className="break-words">{formatSalary(job.salary?.min, job.salary?.max)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Job Description */}
                <div className="p-6 flex-1">
                  <p className="text-gray-700 text-sm leading-relaxed break-words break-all line-clamp-3 mb-4">
                    {stripHTML(job.description || 'No description available')}
                  </p>
                </div>

                {/* Job Footer */}
                <div className="px-6 py-4 border-t border-gray-100 mt-auto">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center text-sm text-gray-500 whitespace-nowrap flex-shrink-0">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>Posted {formatDate(job.createdAt)}</span>
                    </div>
                    <div className="flex space-x-2 flex-shrink-0">
                      <button
                        onClick={() => handleViewJob(job._id)}
                        className="px-3 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => handleApplyNow(job)}
                        className="px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 whitespace-nowrap"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && jobs.length > pageSize && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-10 gap-4">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, jobs.length)} of {jobs.length} jobs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                const isActive = page === currentPage;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-lg text-sm font-semibold ${isActive
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && jobs.length === 0 && (
          <div className="text-center py-20">
            <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Job Openings</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Check back later!
            </p>
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto border border-gray-100">
            <div className="p-6 sm:p-8 space-y-8">
              {/* Modal Header */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div className="space-y-3 flex-1">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 capitalize break-words break-all">
                    {selectedJob.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 text-gray-600">
                    <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold border border-blue-200 capitalize break-words break-all">
                      {selectedJob.department || 'Not specified'}
                    </span>
                    <span className="text-gray-600 font-medium capitalize break-words break-all">
                      {selectedJob.location || 'Not specified'}
                    </span>
                    <span className="text-gray-600 font-medium capitalize break-words break-all">
                      {selectedJob.type ? selectedJob.type.replace('_', ' ') : 'Not specified'}
                    </span>
                    {selectedJob.jobId && (
                      <span className="text-gray-500 text-xs sm:text-sm font-mono tracking-wide break-words break-all">
                        Job ID: {selectedJob.jobId}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-600 hover:text-gray-900 text-4xl p-2 hover:bg-gray-100 rounded-full transition-all duration-200 self-start lg:self-auto"
                >
                  ×
                </button>
              </div>

              {/* Job Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
                    Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Department:</span>
                      <span className="font-semibold text-gray-900 text-right capitalize break-words break-all">
                        {selectedJob.department || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Location:</span>
                      <span className="font-semibold text-gray-900 text-right capitalize break-words break-all">
                        {selectedJob.location || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Job Type:</span>
                      <span className="font-semibold text-gray-900 text-right capitalize break-words break-all">
                        {selectedJob.type ? selectedJob.type.replace('_', ' ') : 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Experience Level:</span>
                      <span className="font-semibold text-gray-900 text-right break-words break-all text-capitalize">
                        {formatExperience(selectedJob.experience)}
                      </span>
                    </div>
                    {formatSalary(selectedJob.salary?.min, selectedJob.salary?.max) !== 'Not specified' && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-600">Salary:</span>
                        <span className="font-semibold text-gray-900 text-right break-words break-all">
                          {formatSalary(selectedJob.salary?.min, selectedJob.salary?.max)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Deadline:</span>
                      <span className="font-semibold text-gray-900 text-right break-words break-all">
                        {selectedJob.deadline ? formatDate(selectedJob.deadline) : 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-green-600" />
                    Application
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Status:</span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${getStatusBadgeClasses(
                          selectedJob.status || 'active'
                        )}`}
                      >
                        {selectedJob.status || 'Active'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Priority:</span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${getPriorityBadgeClasses(
                          selectedJob.priority || 'medium'
                        )}`}
                      >
                        {selectedJob.priority || 'Medium'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Posted On:</span>
                      <span className="font-semibold text-gray-900 text-right break-words break-all">
                        {selectedJob.createdAt || selectedJob.postedDate
                          ? formatDate(selectedJob.createdAt || selectedJob.postedDate)
                          : 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-600">Applications:</span>
                      <span className="font-semibold text-gray-900 text-right break-words break-all">
                        {getApplicationsCount(selectedJob)}
                      </span>
                    </div>
                    {selectedJob.deadline && (
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-600">Closes On:</span>
                        <span className="font-semibold text-gray-900 text-right break-words break-all">
                          {formatDate(selectedJob.deadline)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-6 w-6 mr-3 text-purple-600" />
                  Description
                </h3>
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="prose prose-gray max-w-none break-words break-all">
                    <div
                      className="text-gray-700 leading-relaxed text-base break-words break-all whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedJob.description || 'No description available'),
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Requirements and Skills */}
              <div className="grid grid-cols-1 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="h-6 w-6 mr-3 text-blue-600" />
                    Requirements
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    {selectedJob.requirements && selectedJob.requirements.length > 0 ? (
                      <ul className="space-y-3">
                        {selectedJob.requirements.map((req, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed break-words break-all">
                              {req}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No specific requirements mentioned</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="h-6 w-6 mr-3 text-green-600" />
                    Skills
                  </h3>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    {selectedJob.skills && selectedJob.skills.length > 0 ? (
                      <ul className="space-y-3">
                        {selectedJob.skills.map((skill, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed break-words break-all">
                              {skill}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No specific skills mentioned</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setSelectedJob(null)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 font-semibold transition-all duration-200"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleApplyNow(selectedJob);
                    setSelectedJob(null);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center transform hover:scale-105 whitespace-nowrap"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Status Modal */}
      {showCandidateStatusModal && candidateStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto border border-gray-100">
            <div className="p-6 sm:p-8 space-y-6">
              {/* Modal Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Application Status
                  </h2>
                  <p className="text-gray-600">Candidate ID: <span className="font-mono font-semibold">{candidateStatus.candidateId}</span></p>
                </div>
                <button
                  onClick={() => {
                    setShowCandidateStatusModal(false);
                    setCandidateStatus(null);
                  }}
                  className="text-gray-600 hover:text-gray-900 text-4xl p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
                >
                  ×
                </button>
              </div>

              {/* Candidate Information */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Candidate Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-semibold text-gray-900 text-right capitalize">
                      {candidateStatus.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold text-gray-900 text-right break-all">
                      {candidateStatus.email}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-semibold text-gray-900 text-right">
                      {candidateStatus.phone}
                    </span>
                  </div>
                </div>
              </div>

              {/* Application Details */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-green-600" />
                  Application Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-4">
                    <span className="text-gray-600 flex-shrink-0">Job Title:</span>
                    <span className="font-semibold text-gray-900 text-right capitalize break-words break-all min-w-0 flex-1">
                      {candidateStatus.jobTitle || 'Not specified'}
                    </span>
                  </div>
                  {candidateStatus.job && (
                    <>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-600">Department:</span>
                        <span className="font-semibold text-gray-900 text-right capitalize">
                          {candidateStatus.job.department || candidateStatus.department || 'Not specified'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-semibold text-gray-900 text-right capitalize">
                          {candidateStatus.job.location || 'Not specified'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-600">Job ID:</span>
                        <span className="font-semibold text-gray-900 text-right font-mono">
                          {candidateStatus.job.jobId || candidateStatus.jobId || 'Not specified'}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-600">Status:</span>
                    <span
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${candidateStatus.recruitmentStatus === 'hired'
                        ? 'bg-green-100 text-green-800'
                        : candidateStatus.recruitmentStatus === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : candidateStatus.recruitmentStatus === 'shortlisted' || candidateStatus.recruitmentStatus === 'interview' || candidateStatus.recruitmentStatus === 'interviewed'
                            ? 'bg-blue-100 text-blue-800'
                            : candidateStatus.recruitmentStatus === 'offer' || candidateStatus.recruitmentStatus === 'offered'
                              ? 'bg-purple-100 text-purple-800'
                              : candidateStatus.recruitmentStatus === 'screening'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-yellow-100 text-yellow-800'
                        }`}
                    >
                      {getRecruitmentStatusText(candidateStatus.recruitmentStatus)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-gray-600">Applied Date:</span>
                    <span className="font-semibold text-gray-900 text-right">
                      {candidateStatus.appliedDate
                        ? formatDate(candidateStatus.appliedDate)
                        : 'Not specified'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCandidateStatusModal(false);
                    setCandidateStatus(null);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;